import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { storeService } from '../services/storeService';
import { defaultTheme } from '../config/theme';

export const StoreContext = createContext();

export const useStoreContext = () => {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStoreContext deve ser usado dentro de StoreProvider');
  return ctx;
};

const REVITER_ROOT_HOSTS = new Set([
  'reviter.com',
  'www.reviter.com',
  'reviter.com.br',
  'www.reviter.com.br',
  'localhost',
  '127.0.0.1',
]);
const SUBDOMAIN_SUFFIXES = ['.reviter.com.br', '.reviter.com'];
const STORE_SLUG_KEY = 'reviterCurrentStoreSlug';

// Domínios de plataformas de deploy/preview nunca são storefronts.
// Trata como Reviter root direto, evita 404 desnecessário no getByDomain.
const DEPLOY_HOST_SUFFIXES = [
  '.amplifyapp.com',
  '.vercel.app',
  '.netlify.app',
  '.netlify.com',
  '.github.io',
  '.pages.dev',
];

function hostFromWindow() {
  if (typeof window === 'undefined') return '';
  return (window.location.hostname || '').toLowerCase();
}

function subdomainFromHost(host) {
  for (const suffix of SUBDOMAIN_SUFFIXES) {
    if (host.endsWith(suffix)) {
      const sub = host.slice(0, -suffix.length);
      if (!sub || sub === 'www') return null;
      return sub;
    }
  }
  return null;
}

function isDeployPreviewHost(host) {
  return DEPLOY_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix));
}

// Extrai slug da URL legada `/fornecedor/<slug>/...`. `useParams()` não
// funciona no nível do StoreProvider porque ele fica FORA da Route match.
// Precisa parsear manualmente do pathname.
// `/fornecedor/painel` (área logada do owner) não é slug de loja — ignora.
function slugFromPath(pathname) {
  const match = pathname.match(/^\/fornecedor\/([^/]+)/);
  if (!match) return null;
  if (match[1] === 'painel') return null;
  return match[1];
}

// Override de tenant via `?tenantId=slug` — atalho de dev para simular um
// host de loja sem precisar de subdomínio/custom domain. Persiste em
// sessionStorage para sobreviver a navegações que descartam a query string.
// `?tenantId=` (vazio) limpa o override.
const TENANT_OVERRIDE_KEY = 'reviterTenantOverride';
function tenantOverrideFromQuery(search) {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(search || '');
  if (!params.has('tenantId')) {
    return sessionStorage.getItem(TENANT_OVERRIDE_KEY) || null;
  }
  const raw = (params.get('tenantId') || '').trim().toLowerCase();
  if (!raw) {
    sessionStorage.removeItem(TENANT_OVERRIDE_KEY);
    return null;
  }
  sessionStorage.setItem(TENANT_OVERRIDE_KEY, raw);
  return raw;
}

// Parse defensivo do theme — em MariaDB o JSON às vezes vem como string
// mesmo com o getter do modelo. Backup client-side pra nunca quebrar.
function normalizeStoreTheme(store) {
  if (!store || !store.theme) return store;
  if (typeof store.theme === 'string') {
    try {
      return { ...store, theme: JSON.parse(store.theme) };
    } catch {
      return { ...store, theme: null };
    }
  }
  return store;
}

export const StoreProvider = ({ children }) => {
  const location = useLocation();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const pathSlug = slugFromPath(location.pathname);
  const tenantOverride = tenantOverrideFromQuery(location.search);

  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      setLoading(true);
      setError(null);
      const host = hostFromWindow();

      // 1. Rota legada /fornecedor/:supplierSlug → resolve por slug
      // 2. Override `?tenantId=slug` (dev) → resolve por slug
      const slug = pathSlug || tenantOverride;
      if (slug) {
        try {
          const raw = await storeService.getBySlug(slug);
          const s = normalizeStoreTheme(raw);
          if (!cancelled) {
            setStore(s);
            localStorage.setItem(STORE_SLUG_KEY, s.slug);
          }
        } catch (e) {
          if (!cancelled) {
            setStore(null);
            setError(e.message);
            localStorage.removeItem(STORE_SLUG_KEY);
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }

      // 3. Host é reviter.com / localhost / domínio de preview deploy
      //    (amplifyapp, vercel, netlify etc) → site principal Reviter
      if (
        (REVITER_ROOT_HOSTS.has(host) && !subdomainFromHost(host)) ||
        isDeployPreviewHost(host)
      ) {
        if (!cancelled) {
          setStore(null);
          localStorage.removeItem(STORE_SLUG_KEY);
          setLoading(false);
        }
        return;
      }

      // 4. Custom domain ou subdomínio → resolve via backend por host
      try {
        const raw = await storeService.getByDomain(host);
        const s = normalizeStoreTheme(raw);
        if (!cancelled) {
          setStore(s);
          localStorage.setItem(STORE_SLUG_KEY, s.slug);
        }
      } catch (e) {
        if (!cancelled) {
          setStore(null);
          setError(e.message);
          localStorage.removeItem(STORE_SLUG_KEY);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    resolve();
    return () => { cancelled = true; };
  }, [location.pathname, location.search, pathSlug, tenantOverride]);

  const value = useMemo(() => {
    const businessType = store?.businessType || null;
    const theme = store?.theme || defaultTheme;
    return {
      store,
      theme,
      loading,
      error,
      businessType,
      isReviterSite: !store,
      isStoreSite: !!store,
      isSellerStore: businessType === 'seller',
      isResellerStore: businessType === 'reseller',
      isCatalogStore: businessType === 'catalog'
    };
  }, [store, loading, error]);

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
};
